import { Component } from '@theme/component';
import {
  prefersReducedMotion,
  debounce,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Returns only the media slide elements inside the dialog modal.
   * @returns {HTMLElement[]}
   */
  get zoomMedia() {
    const list = this.refs.dialog?.querySelector('.dialog-zoomed-gallery');
    if (list) {
      return Array.from(list.children);
    }
    return Array.from(this.refs.media || []);
  }

  /**
   * Opens the zoom dialog cleanly and positions the selected image.
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} [event] - The pointer event.
   */
  async open(index, event) {
    if (event) event.preventDefault();

    const { dialog, thumbnails } = this.refs;
    const media = this.zoomMedia;
    const targetImage = media[index];
    const targetThumbnail = thumbnails?.children[index];

    if (!dialog) return;

    dialog.showModal();

    if (targetImage) {
      const container = dialog.querySelector('.dialog-zoomed-gallery');
      if (container) {
        container.scrollLeft = targetImage.offsetLeft;
      }
    }

    if (thumbnails && targetThumbnail) {
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: 'instant',
        block: 'center',
        inline: 'center',
      });
    }

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer || !mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles scroll event to update active thumbnail
   */
  handleScroll = debounce(async () => {
    const { thumbnails } = this.refs;
    const media = this.zoomMedia;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails?.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  close() {
    const { dialog } = this.refs;
    if (dialog) {
      dialog.close();
    }
    window.dispatchEvent(new DialogCloseEvent());
  }

  closeDialog() {
    this.close();
  }

  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  async handleThumbnailPointerEnter(index) {
    const media = this.zoomMedia;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Selects thumbnail & scrolls gallery directly to target image
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { thumbnails, dialog } = this.refs;
    const media = this.zoomMedia;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];
    const container = dialog?.querySelector('.dialog-zoomed-gallery');

    if (targetImage && container) {
      container.scrollTo({
        left: targetImage.offsetLeft,
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Previous Button Handler
   */
  async prev() {
    const media = this.zoomMedia;
    const mostVisible = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisible);
    if (activeIndex > 0) {
      this.selectThumbnail(activeIndex - 1);
    }
  }

  /**
   * Next Button Handler
   */
  async next() {
    const media = this.zoomMedia;
    const mostVisible = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisible);
    if (activeIndex < media.length - 1) {
      this.selectThumbnail(activeIndex + 1);
    }
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}